import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';
import {Store} from '@ngrx/store';
import {takeUntil} from 'rxjs/operators';
import {AppState, selectFooterAction, selectUser} from '@store/reducers';
import {UserService} from '@services/user.service';
import {AuthService} from '@services/auth.service';
import {Subject} from 'rxjs/Subject';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {FormValidationService} from '@services/form-validation.service';
import {User} from '@models/user.interface';
import {FooterActionEnum} from '@models/footer-action.enum';
import {EditUser} from '@store/actions/profile.action';
import {PrintFormatEnum, PrintSettingsEnum} from '@models/print.enum';

@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss']
})
export class UserSettingsComponent implements OnInit, OnDestroy {
  userDetails: any = {};
  errors: any;
  newPassword: any;
  subscription: Subscription;
  private destroy$: Subject<boolean> = new Subject();
  submitted: boolean;
  submittedPassword: boolean;

  public form: FormGroup;

  constructor(
    private store: Store<AppState>,
    private auth: AuthService,
    private userService: UserService,
    private formValidation: FormValidationService,
  ) {}

  ngOnInit() {

    this.store.select(selectUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: User) => {
        this.userDetails = data;
        this.form = this.generateForm();
      });

    this.store.select(selectFooterAction)
      .pipe(takeUntil(this.destroy$))
      .subscribe(action => {
        if (action === FooterActionEnum.SAVE_SETTINGS) {
          this.save();
        }
      });
  }

  generateForm() {
    if (!this.userDetails) {
      return;
    }
    return new FormGroup({
      company_information: this.generateCompanyInformation(),
      account_settings: this.generateAccountSettings(),
      payment_settings: this.generatePaymentSettings(),
      printer_settings: this.generatePrinterSettings(),
      misc_settings: this.generateMiscSettings(),
      consignment_settings: this.generateConsignmentSettings(),
      // printer_settings: this.generatePrinterSettings(),
      change_password: this.generateСhangePassword()
    });
  }

  generateCompanyInformation() {
    const companyFields: any = this.userDetails.is_company === 1 ? {
      company_name: new FormControl(this.userDetails.company_name, Validators.required),
      register_num: new FormControl({ value: this.userDetails.register_num, disabled: true}),
    } : {};
    const {
      address, invoice_address,
      zip, invoice_zip,
      city, invoice_city,
      country_code, invoice_country_code
    } = this.userDetails;
    const postalAddressExist =
      address && address !== invoice_address ||
      zip && zip !== invoice_zip ||
      city && city !== invoice_city ||
      country_code && country_code !== invoice_country_code;
    return new FormGroup({
        ...companyFields,
      address: new FormControl({
        value: postalAddressExist ? this.userDetails.address : this.userDetails.invoice_address,
        disabled: !postalAddressExist
      }, Validators.required),
      zip: new FormControl({
        value: postalAddressExist ? this.userDetails.zip : this.userDetails.invoice_zip,
        disabled: !postalAddressExist
      }, Validators.required),
      city: new FormControl({
        value: postalAddressExist ? this.userDetails.city : this.userDetails.invoice_city,
        disabled: !postalAddressExist
      }, Validators.required),
      country_code: new FormControl({
        value: postalAddressExist ? this.userDetails.country_code : this.userDetails.invoice_country_code,
        disabled: !postalAddressExist
      }, Validators.required),
      invoice_address: new FormControl(this.userDetails.invoice_address, Validators.required),
      invoice_zip: new FormControl(this.userDetails.invoice_zip, Validators.required),
      invoice_city: new FormControl(this.userDetails.invoice_city, Validators.required),
      invoice_country_code: new FormControl(this.userDetails.invoice_country_code, Validators.required),
      same_as_invoice_address: new FormControl(!postalAddressExist),

    });
  }
  generateAccountSettings() {
    return new FormGroup({
      name: new FormControl(this.userDetails.name, [Validators.required, Validators.minLength(3)]),
      surname: new FormControl(this.userDetails.surname, Validators.required),
      email: new FormControl(this.userDetails.email,  Validators.required),
      phone: new FormControl(this.userDetails.phone, Validators.required),
    });
  }

  generatePrinterSettings() {
    const printSetting = this.userDetails.settings.unifaun_print_settings;
    const printFormat = this.userDetails.settings.unifaun_print_format;

    return new FormGroup({
      unifaun_print_settings: new FormControl(printSetting, Validators.required),
      unifaun_print_format: new FormControl({
        value: printFormat === PrintFormatEnum.ZPL,
        disabled: printSetting !== PrintSettingsEnum.LABEL && printFormat !== PrintFormatEnum.ZPL
      })
    });
  }

  generatePaymentSettings() {
    const defaultPaymentMethod = parseInt(this.userDetails.settings && this.userDetails.settings.default_payment_method || 1, 10);
    return new FormGroup({
      default_payment_method: new FormControl(defaultPaymentMethod)
    });
  }

  generateСhangePassword() {
    return new FormGroup({
      current_password: new FormControl('', Validators.required),
      password: new FormControl('', [Validators.required, Validators.minLength(6)]),
      password_confirmation: new FormControl('', [Validators.required, Validators.minLength(6)]),
    }, { validators: this.formValidation.comparePasswords });
  }

  generateMiscSettings() {
    const insuranceDefault = parseInt(this.userDetails.settings && this.userDetails.settings.insurance_selected_default || 1, 10);

    return new FormGroup({
      insurance_selected_default: new FormControl(!!parseInt(this.userDetails.settings.insurance_selected_default, 10)),
      email_order_receipt: new FormControl(!!parseInt(this.userDetails.settings.email_order_receipt, 10)),
      email_freight_documents: new FormControl(!!parseInt(this.userDetails.settings.email_freight_documents, 10)),
      shipment_table_column_title: new FormControl(!!parseInt(this.userDetails.settings.shipment_table_column_title, 10))

    });
  }

  generateConsignmentSettings() {
    return new FormGroup({
      parcel_content: new FormControl(this.userDetails.settings.parcel_content),
      parcel_value: new FormControl(this.userDetails.settings.parcel_value)
    });
  }

  save() {
    if (this.form.get('company_information').invalid || this.form.get('account_settings').invalid) {
      this.submitted = true;
      return;
    }
    const formValue = this.form.getRawValue();

    formValue.printer_settings.unifaun_print_format = formValue.printer_settings.unifaun_print_format
      ? PrintFormatEnum.ZPL : PrintFormatEnum.PDF;

    // Convert to integer
    formValue.misc_settings.email_order_receipt = formValue.misc_settings.email_order_receipt ? 1 : 0;
    formValue.misc_settings.email_freight_documents = formValue.misc_settings.email_freight_documents ? 1 : 0;
    formValue.misc_settings.insurance_selected_default = formValue.misc_settings.insurance_selected_default ? 1 : 0;
    formValue.misc_settings.shipment_table_column_title = formValue.misc_settings.shipment_table_column_title ? 1 : 0;


    const data: any = {
      ...formValue.company_information,
      ...formValue.account_settings,
      settings: {
        ...formValue.payment_settings,
        ...formValue.misc_settings,
        ...formValue.printer_settings,
        ...formValue.consignment_settings
      },
      same_as_invoice_address: formValue.company_information.same_as_invoice_address ? 1 : 0,

    };
    delete data.register_num;
    if (data.same_as_invoice_address) {
      delete data.country_code;
      delete data.zip;
      delete data.address;
      delete data.city;
    }

    this.store.dispatch(new EditUser(data));
    if (this.changePassword) {
      this.submittedPassword = true;
    }
    if (this.form.get('change_password').valid) {
      this.userService.changePassword({...formValue.change_password}).subscribe((res) => {});
    }
  }

  get changePassword() {
    let data = this.form.getRawValue().change_password;
    return data.current_password || data.password || data.confirm_password;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/user-settings/user-settings.component.ts